/**
 * @copyright Copyright (c) 2015, Pacific Biosciences of California, Inc.
 * @author <a href="mailto:bskinner@pacificbiosciences.com">Brian Skinner</a>
 */

import { Facet } from "../facets/facet"
import { ALIGNMENT_FACET } from "../facets/alignment"
import { BARCODE_FACET } from "../facets/barcode"
import { CCS_READ_FACET } from "../facets/ccs-read"
import { SUBREAD_FACET } from "../facets/subread"
import { HDF_SUBREAD_FACET } from "../facets/hdf-subread"
import { REFERENCE_FACET } from "../facets/reference"
import { GMAP_REFERENCE_FACET } from "../facets/gmap-reference"
import { BED_FACET } from "../facets/bedfile"

export interface IDatasetType {
    filetype: string
    shortName: string
    title: string
    browseForTitle?: string
    filter?: string
    facet: Facet | null
}

export class DatasetType {

    static ALIGNMENT = new DatasetType({
        filetype: "PacBio.DataSet.AlignmentSet",
        shortName: "alignments",
        title: "Aligned Data",
        facet: ALIGNMENT_FACET
    })

    static BARCODE = new DatasetType({
        filetype: "PacBio.DataSet.BarcodeSet",
        shortName: "barcodes",
        title: "Barcodes",
        browseForTitle: "Select a Barcode data file (*.barcode.xml):",
        filter: "barcodeset.xml",
        facet: BARCODE_FACET
    })

    static CONSENSUS_READ = new DatasetType({
        filetype: "PacBio.DataSet.ConsensusReadSet",
        shortName: "ccsreads",
        title: "HiFi reads",
        facet: CCS_READ_FACET
    })

    static CONSENSUS_ALIGNMENT = new DatasetType({
        filetype: "PacBio.DataSet.ConsensusAlignmentSet",
        shortName: "ccsalignments",
        title: "Consensus Alignment Data",
        facet: null
    })

    static CONTIG = new DatasetType({
        filetype: "PacBio.DataSet.ContigSet",
        shortName: "contigs",
        title: "Contigs",
        facet: null
    })

    // XXX leave this in for backwards compatibility with old jobs - but
    // we should never offer this as an option when importing files
    static HDF_SUBREAD = new DatasetType({
        filetype: "PacBio.DataSet.HdfSubreadSet",
        shortName: "hdfsubreads",
        title: "RS II Data",
        browseForTitle: "Select a RS II sequence data file (*.hdfsubreadset.xml):",
        filter: "hdfsubreadset.xml",
        facet: HDF_SUBREAD_FACET
    })

    static REFERENCE_FASTA = new DatasetType({
        filetype: "PacBio.DataSet.ReferenceSet",
        shortName: "references",
        title: "Reference",
        browseForTitle: "Select a Reference file (*.fa, *.fasta, *.fsa):",
        filter: ".fa,.fasta,.fsa",
        facet: REFERENCE_FACET
    })

    static REFERENCE = new DatasetType({
        filetype: "PacBio.DataSet.ReferenceSet",
        shortName: "references",
        title: "References",
        browseForTitle: "Select a ReferenceSet file (*.xml):",
        filter: ".xml",
        facet: REFERENCE_FACET
    })

    static GMAP_REFERENCE = new DatasetType({
        filetype: "PacBio.DataSet.GmapReferenceSet",
        shortName: "gmapreferences",
        title: "Gmap Reference",
        browseForTitle: "Select a GMap Reference file (*.xml):",
        filter: ".xml",
        facet: GMAP_REFERENCE_FACET
    })

    static TRANSCRIPT = new DatasetType({
        filetype: "PacBio.DataSet.TranscriptSet",
        shortName: "Transcripts",
        title: "RNA Transcripts",
        browseForTitle: "Select a TranscriptSet file (*.transcriptset.xml):",
        filter: "transcriptset.xml",
        facet: null
    })

    static TRANSCRIPT_ALIGNMENT = new DatasetType({
        filetype: "PacBio.DataSet.TranscriptAlignmentSet",
        shortName: "transcriptalignments",
        title: "RNA Transcript AlignmentSets",
        browseForTitle: "Select a TranscriptAlignmentSet file (*.transcriptalignmentset.xml):",
        filter: "transcriptalignmentset.xml",
        facet: null
    })

    static SUBREAD = new DatasetType({
        filetype: "PacBio.DataSet.SubreadSet",
        shortName: "subreads",
        title: "Subreads",
        browseForTitle: "Select a Sequel sequence data file (*.subreadset.xml):",
        filter: ".subreadset.xml",
        facet: SUBREAD_FACET
    })

    static BED = new DatasetType({
        filetype: "PacBio.DataSet.BedSet",
        shortName: "bedfiles",
        title: "Target regions",
        browseForTitle: "Select a Target Region dataset (*.bedset.xml):",
        filter: ".bedset.xml",
        facet: BED_FACET
    })

    static DATASET_TYPES: DatasetType[] = [
        DatasetType.ALIGNMENT,
        DatasetType.BARCODE,
        DatasetType.CONSENSUS_READ,
        DatasetType.CONSENSUS_ALIGNMENT,
        DatasetType.CONTIG,
        DatasetType.HDF_SUBREAD,
        DatasetType.REFERENCE,
        DatasetType.GMAP_REFERENCE,
        DatasetType.TRANSCRIPT,
        DatasetType.SUBREAD,
        DatasetType.BED
    ]

    public filetype: string
    public shortName: string
    public title: string
    public browseForTitle: string
    public filter: string
    public facet: Facet | null

    static DATASET_TYPE_TITLES(): string[] {
        return DatasetType.DATASET_TYPES.map( datasetType => {
            return datasetType.title
        })
    }

    constructor(datasetType: IDatasetType) {
        this.filetype = datasetType.filetype
        this.shortName = datasetType.shortName
        this.title = datasetType.title
        this.browseForTitle = datasetType.browseForTitle || ""
        this.filter = datasetType.filter || ""
    }

    static byShortName(shortName: string) {
        return DatasetType.by("shortName", shortName)
    }

    static byFiletype(filetype: string) {
        return DatasetType.by("filetype", filetype)
    }

    static shortNameForFileType = (fileType: string): string => {
        const type: DatasetType = DatasetType.byFiletype(fileType)
        if (!type) { return "ERROR" }
        return type.shortName
    }

    private static by(prop: string, value: string) {
        for (let datasetType of DatasetType.DATASET_TYPES) {
            if (datasetType[prop] === value) {
                return datasetType
            }
        }
        return null
    }
}

export const isFileTypePrimary = (fileTypeId: string): boolean =>
    fileTypeId === DatasetType.SUBREAD.filetype || fileTypeId === DatasetType.CONSENSUS_READ.filetype
